/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reatomMap, withAssign } from '@reatom/framework'

import { Set } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'

import { FavoriteId, PairAddress } from '../model'

import type { UserApi } from '@root/shared/api'

export const reatomFavoriteMeta = (dto: UserApi.FavoritePairMetaDto) => {
  const favoriteId = FavoriteId(dto.favoriteId)
  const createdAt = new Date(dto.createdAt)
  const pairAddress = PairAddress(dto.pairAddress)

  const indexPositionAtom = atom(dto.indexPosition)
  const updatedAtAtom = atom(new Date(dto.updatedAt))

  const updateFromDtoAction = action(
    (ctx, dto: UserApi.FavoritePairMetaDto) => {
      indexPositionAtom(ctx, dto.indexPosition)
      updatedAtAtom(ctx, new Date(dto.updatedAt))
    },
  )

  return {
    favoriteId,
    pairAddress,
    createdAt,
    updatedAt: updatedAtAtom,

    indexPosition: indexPositionAtom,
    updateFromDto: updateFromDtoAction,
  }
}
export type FavoriteMetaModel = ReturnType<typeof reatomFavoriteMeta>

const reatomFavoriteMetasModel = ({
  network,
}: {
  network: Network.Network
}) => {
  const byPairAtom = reatomMap<PairAddress, FavoriteMetaModel>()

  const updateFromDtosAction = action(
    (ctx, dtos: Array<UserApi.FavoritePairMetaDto>) => {
      const dtoIds = Set.create()
      for (const dto of dtos) {
        dtoIds.add(dto.favoriteId)
        const meta = byPairAtom.get(ctx, PairAddress(dto.pairAddress))
        if (meta) {
          meta.updateFromDto(ctx, dto)
          continue
        }
        const newMeta = reatomFavoriteMeta(dto)
        byPairAtom.set(ctx, PairAddress(dto.pairAddress), newMeta)
      }

      const byPair = ctx.get(byPairAtom)
      for (const meta of byPair.values()) {
        if (!dtoIds.has(meta.favoriteId)) {
          byPairAtom.delete(ctx, meta.pairAddress)
        }
      }
    },
  )

  return byPairAtom.pipe(
    withAssign(() => ({
      network,
      updateFromDtos: updateFromDtosAction,
    })),
  )
}

export const favoriteMetasModelByNetwork = Network.byNetworkRecord((network) =>
  reatomFavoriteMetasModel({ network }),
)

export const favoriteMetasModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  return favoriteMetasModelByNetwork[network]
}, 'favoriteMetasModelAtom')

export const favoriteMetasAtom = atom((ctx) => {
  const model = ctx.spy(favoriteMetasModelAtom)
  return ctx.spy(model)
}, 'favoriteMetasAtom')

export const favoritePairAddressesAtom = atom((ctx) => {
  const metas = ctx.spy(favoriteMetasAtom)
  return Set.create(metas.keys())
}, 'favoritePairAddressesAtom')
